@file:OptIn(ExperimentalMaterial3Api::class)

package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.ImageFromUri
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.ContentType
import pl.zar.tikthrow.viewmodel.QueueViewModel
import pl.zar.tikthrow.viewmodel.QueuedPost
import pl.zar.tikthrow.viewmodel.SavedPost
import pl.zar.tikthrow.viewmodel.SavedPostsViewModel
import pl.zar.tikthrow.viewmodel.ScheduledPost
import pl.zar.tikthrow.viewmodel.ScheduledPostsViewModel

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

private val platformColors = mapOf(
    Platform.TWITTER   to Color(0xFF1D9BF0),
    Platform.INSTAGRAM to Color(0xFFE1306C),
    Platform.FACEBOOK  to Color(0xFF1877F2),
    Platform.TIKTOK    to Color(0xFFFF0050),
    Platform.YOUTUBE   to Color(0xFFFF0000),
    Platform.DISCORD   to Color(0xFF5865F2),
)

private const val STATUS_PENDING = "PENDING"
private const val STATUS_FAILED = "FAILED"

/**
 * The Kolejka tab: the ordered backlog of posts the publishing plan drains, plus the scheduled
 * and saved drafts that can be pulled into it. Every state shown here comes from a real view
 * model — no placeholder data — so the list is exactly what the scheduler will eventually publish.
 */
@Composable
fun QueueScreen(
    modifier: Modifier,
    viewModel: SavedPostsViewModel,
    scheduledViewModel: ScheduledPostsViewModel,
    queueViewModel: QueueViewModel,
    onNavigateToAddQueuedPost: () -> Unit,
    onNavigateToEditPlan: () -> Unit,
    onNavigateToAccounts: () -> Unit,
    onEditPost: (String) -> Unit,
    onEditScheduledPost: (String, ContentType) -> Unit,
) {
    val queue by queueViewModel.queue.collectAsState()
    val publishFeedback by queueViewModel.publishFeedback.collectAsState()
    val scheduledPosts by scheduledViewModel.posts.collectAsState()
    val savedPosts by viewModel.posts.collectAsState()

    TabScreenBackground(modifier = modifier) {
        Column(modifier = Modifier.fillMaxSize()) {
            QueueTopBar(
                onNavigateToEditPlan = onNavigateToEditPlan,
                onNavigateToAccounts = onNavigateToAccounts,
            )

            publishFeedback?.let { feedback ->
                PublishFeedbackBanner(
                    message = feedback,
                    onDismiss = queueViewModel::clearFeedback,
                )
            }

            Box(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentAlignment = Alignment.TopCenter
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = 720.dp)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(20.dp)
                ) {
                    QueueActions(
                        onAddPost = onNavigateToAddQueuedPost,
                        onEditPlan = onNavigateToEditPlan,
                        onAccounts = onNavigateToAccounts,
                    )

                    QueueSection(
                        queue = queue,
                        onAddPost = onNavigateToAddQueuedPost,
                        onMoveUp = queueViewModel::moveUp,
                        onMoveDown = queueViewModel::moveDown,
                        onRemove = queueViewModel::removePost,
                        onRetry = queueViewModel::retryPost,
                        onEdit = onEditPost,
                    )

                    if (scheduledPosts.isNotEmpty()) {
                        ScheduledSection(
                            posts = scheduledPosts,
                            onEditScheduledPost = onEditScheduledPost,
                        )
                    }

                    if (savedPosts.isNotEmpty()) {
                        DraftsSection(
                            posts = savedPosts,
                            onEditPost = onEditPost,
                        )
                    }

                    Spacer(Modifier.height(8.dp))
                }
            }
        }
    }
}

@Composable
private fun QueueTopBar(
    onNavigateToEditPlan: () -> Unit,
    onNavigateToAccounts: () -> Unit,
) {
    Surface(color = Slate100, shadowElevation = 2.dp) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = "Kolejka",
                style = TextStyle(brush = gradientBrush, fontSize = 22.sp, fontWeight = FontWeight.ExtraBold),
                modifier = Modifier.weight(1f)
            )
            TextButton(onClick = onNavigateToEditPlan) {
                Text("Edytuj plan", color = GradientStart, fontWeight = FontWeight.Bold, fontSize = 13.sp)
            }
            TextButton(onClick = onNavigateToAccounts) {
                Text("Konta", color = GradientStart, fontWeight = FontWeight.Bold, fontSize = 13.sp)
            }
        }
    }
}

@Composable
private fun PublishFeedbackBanner(
    message: String,
    onDismiss: () -> Unit,
) {
    Surface(
        color = Rose500.copy(alpha = 0.12f),
        border = BorderStroke(1.dp, Rose500.copy(alpha = 0.4f)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Icon(Icons.Filled.Warning, contentDescription = null, tint = Rose500, modifier = Modifier.size(18.dp))
            Text(
                text = message,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                color = Slate900,
                modifier = Modifier.weight(1f)
            )
            IconButton(onClick = onDismiss, modifier = Modifier.size(24.dp)) {
                Icon(Icons.Filled.Close, contentDescription = "Zamknij", tint = Slate500, modifier = Modifier.size(16.dp))
            }
        }
    }
}

@Composable
private fun QueueActions(
    onAddPost: () -> Unit,
    onEditPlan: () -> Unit,
    onAccounts: () -> Unit,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        Surface(
            onClick = onAddPost,
            shape = RoundedCornerShape(6.dp),
            color = Color.Transparent,
            modifier = Modifier.weight(2f)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(gradientBrush)
                    .padding(vertical = 12.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.White)
                Spacer(Modifier.width(6.dp))
                Text("Dodaj post do kolejki", color = Color.White, fontSize = 13.sp, fontWeight = FontWeight.ExtraBold)
            }
        }
        OutlinedActionButton(label = "Edytuj plan", onClick = onEditPlan, modifier = Modifier.weight(1f))
        OutlinedActionButton(label = "Konta", onClick = onAccounts, modifier = Modifier.weight(1f))
    }
}

@Composable
private fun OutlinedActionButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(6.dp),
        color = Slate100,
        border = BorderStroke(1.5.dp, Slate300),
        modifier = modifier
    ) {
        Box(
            modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp, horizontal = 8.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(label, fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Slate800, maxLines = 1)
        }
    }
}

@Composable
private fun SectionLabel(text: String) {
    Text(
        text = text,
        fontSize = 11.sp,
        fontWeight = FontWeight.ExtraBold,
        letterSpacing = 1.5.sp,
        color = Slate400
    )
}

@Composable
private fun QueueSection(
    queue: List<QueuedPost>,
    onAddPost: () -> Unit,
    onMoveUp: (String) -> Unit,
    onMoveDown: (String) -> Unit,
    onRemove: (String) -> Unit,
    onRetry: (String) -> Unit,
    onEdit: (String) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        SectionLabel(if (queue.isEmpty()) "KOLEJKA" else "KOLEJKA · ${queue.size}")
        if (queue.isEmpty()) {
            Surface(
                shape = RoundedCornerShape(8.dp),
                color = Slate100,
                border = BorderStroke(1.dp, Slate200),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "Kolejka jest pusta",
                        fontSize = 15.sp,
                        fontWeight = FontWeight.Bold,
                        color = Slate900
                    )
                    Text(
                        text = "Dodaj treści, a plan publikacji wypuści je w swoich slotach.",
                        fontSize = 12.sp,
                        color = Slate500
                    )
                    Surface(
                        onClick = onAddPost,
                        shape = RoundedCornerShape(6.dp),
                        color = Color.Transparent
                    ) {
                        Box(
                            modifier = Modifier
                                .background(gradientBrush)
                                .padding(horizontal = 20.dp, vertical = 10.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text("Dodaj post do kolejki", color = Color.White, fontSize = 13.sp, fontWeight = FontWeight.ExtraBold)
                        }
                    }
                }
            }
        } else {
            queue.forEachIndexed { index, post ->
                QueueItemCard(
                    post = post,
                    position = index + 1,
                    isFirst = index == 0,
                    isLast = index == queue.lastIndex,
                    onMoveUp = { onMoveUp(post.id) },
                    onMoveDown = { onMoveDown(post.id) },
                    onRemove = { onRemove(post.id) },
                    onRetry = { onRetry(post.id) },
                    onEdit = { onEdit(post.id) },
                )
            }
        }
    }
}

@Composable
private fun QueueItemCard(
    post: QueuedPost,
    position: Int,
    isFirst: Boolean,
    isLast: Boolean,
    onMoveUp: () -> Unit,
    onMoveDown: () -> Unit,
    onRemove: () -> Unit,
    onRetry: () -> Unit,
    onEdit: () -> Unit,
) {
    val isFailed = post.status == STATUS_FAILED
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = Slate100,
        border = if (isFailed) BorderStroke(1.dp, Rose500.copy(alpha = 0.5f)) else BorderStroke(1.dp, Slate200),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Box(
                    modifier = Modifier.size(28.dp).clip(CircleShape).background(Slate200),
                    contentAlignment = Alignment.Center
                ) {
                    Text("$position", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Slate800)
                }
                if (post.media.isNotEmpty()) {
                    Box(modifier = Modifier.size(40.dp).clip(RoundedCornerShape(4.dp)).background(Slate200)) {
                        ImageFromUri(
                            uri = post.media.first().uri,
                            isVideo = post.media.first().isVideo,
                            modifier = Modifier.matchParentSize()
                        )
                    }
                }
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text(
                        text = post.title.ifBlank { post.caption.ifBlank { "(bez tytułu)" } },
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Slate900,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (post.caption.isNotBlank() && post.title.isNotBlank()) {
                        Text(
                            text = post.caption,
                            fontSize = 12.sp,
                            color = Slate500,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
                QueueStatusBadge(status = post.status, type = post.type)
            }

            if (post.platforms.isNotEmpty()) {
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    post.platforms.forEach { platform ->
                        PlatformChip(platform = platform)
                    }
                }
            }

            if (isFailed && post.errorMessage != null) {
                Text(
                    text = post.errorMessage,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Rose500
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                QueueItemAction(Icons.Filled.KeyboardArrowUp, "W górę", enabled = !isFirst, onClick = onMoveUp)
                QueueItemAction(Icons.Filled.KeyboardArrowDown, "W dół", enabled = !isLast, onClick = onMoveDown)
                QueueItemAction(Icons.Filled.Edit, "Edytuj", onClick = onEdit)
                if (isFailed) {
                    QueueItemAction(Icons.Filled.Refresh, "Ponów", onClick = onRetry, tint = Emerald500)
                }
                Spacer(Modifier.weight(1f))
                QueueItemAction(Icons.Filled.Close, "Usuń", onClick = onRemove, tint = Rose500)
            }
        }
    }
}

@Composable
private fun QueueItemAction(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    contentDescription: String,
    enabled: Boolean = true,
    tint: Color = Slate600,
    onClick: () -> Unit,
) {
    Surface(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(6.dp),
        color = Slate200
    ) {
        Icon(
            imageVector = icon,
            contentDescription = contentDescription,
            tint = if (enabled) tint else Slate400,
            modifier = Modifier.padding(6.dp).size(18.dp)
        )
    }
}

@Composable
private fun QueueStatusBadge(status: String, type: ContentType) {
    val (label, color) = when (status) {
        STATUS_FAILED -> "Błąd" to Rose500
        STATUS_PENDING -> "Oczekuje" to Amber400
        "PUBLISHING" -> "Publikuje" to GradientStart
        else -> status to Slate500
    }
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(text = type.label, fontSize = 10.sp, fontWeight = FontWeight.SemiBold, color = Slate500)
        Surface(shape = RoundedCornerShape(4.dp), color = color.copy(alpha = 0.14f)) {
            Text(
                text = label,
                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                color = color
            )
        }
    }
}

@Composable
private fun PlatformChip(platform: Platform) {
    val color = platformColors[platform] ?: Slate600
    Surface(shape = RoundedCornerShape(4.dp), color = Slate200) {
        Row(
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 3.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(5.dp)
        ) {
            Box(modifier = Modifier.size(8.dp).clip(RoundedCornerShape(2.dp)).background(color))
            Text(platform.name, fontSize = 10.sp, fontWeight = FontWeight.SemiBold, color = Slate600)
        }
    }
}

@Composable
private fun ScheduledSection(
    posts: List<ScheduledPost>,
    onEditScheduledPost: (String, ContentType) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        SectionLabel("ZAPLANOWANE · ${posts.size}")
        posts.forEach { post ->
            SimplePostRow(
                title = post.title.ifBlank { post.caption.ifBlank { "(bez tytułu)" } },
                subtitle = buildString {
                    append(post.type.label)
                    append(" · ")
                    append(post.scheduledHour.toString().padStart(2, '0'))
                    append(":")
                    append(post.scheduledMinute.toString().padStart(2, '0'))
                },
                onClick = { onEditScheduledPost(post.id, post.type) }
            )
        }
    }
}

@Composable
private fun DraftsSection(
    posts: List<SavedPost>,
    onEditPost: (String) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        SectionLabel("SZKICE · ${posts.size}")
        posts.forEach { post ->
            SimplePostRow(
                title = post.caption.ifBlank { "(bez opisu)" },
                subtitle = if (post.platforms.isEmpty()) "Szkic"
                           else post.platforms.joinToString(", ") { it.name },
                onClick = { onEditPost(post.id) }
            )
        }
    }
}

@Composable
private fun SimplePostRow(
    title: String,
    subtitle: String,
    onClick: () -> Unit,
) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(8.dp),
        color = Slate100,
        border = BorderStroke(1.dp, Slate200),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Icon(Icons.Filled.Edit, contentDescription = null, tint = Slate400, modifier = Modifier.size(16.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    text = title,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Slate900,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = subtitle,
                    fontSize = 11.sp,
                    color = Slate500,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Icon(Icons.Filled.Check, contentDescription = "Edytuj", tint = GradientStart, modifier = Modifier.size(16.dp))
        }
    }
}
